import {
    GET_JOBS_URL,
    HIDE_CSS_CLASS,
    URL_PARAMS_SEPERATOR,
    REGION_FILTER_NAME,
} from './constants';

export default class Utils {
    constructor({
        disableFetchJobs = false,
        onLoadingCallbacks = [],
        getPagination,
    }) {
        this.fetchIsLoading = false;
        this.disableFetchJobs = disableFetchJobs;
        this.onFetchResultCallbacks = [];
        this.onLoadingCallbacks = onLoadingCallbacks;
        this.getPagination = getPagination;
        this.abortController = new AbortController();
    }

    addCallbackToOnFetchResult(callback) {
        this.onFetchResultCallbacks.push(callback);
    }

    addCallbackToOnLoading(callback) {
        this.onLoadingCallbacks?.push(callback);
    }

    setDisableFetchJobs(val) {
        this.disableFetchJobs = val;
    }

    abortFetch() {
        this.abortController?.abort();
        this.fetchIsLoading = false;
        this.abortController = new AbortController();
    }

    setFilterBadgeNumbers(filters, number) {
        filters.forEach((filter) => {
            this.setFilterBadgeNumber(filter, number);
        });
    }

    setFilterBadgeNumber(filterElement, number) {
        const badge = filterElement.querySelector('.badge');
        const badgeNumber = number;
        badgeNumber && badgeNumber > 0
            ? badge.classList.remove(HIDE_CSS_CLASS)
            : badge.classList.add(HIDE_CSS_CLASS);
        badge.innerHTML = badgeNumber;
    }

    getUrlParams() {
        const searchParams = window.location.search;
        return new URLSearchParams(searchParams ? searchParams.toString() : '');
    }

    updateURLParams(paramStr = '') {
        const newParams = new URLSearchParams(paramStr);
        const newRelativePathQuery =
            window.location.pathname +
            (paramStr ? '?' + newParams.toString() : '');
        history.pushState(null, '', newRelativePathQuery);
    }

    resetFetch() {
        if (this.onFetchResultCallbacks) {
            this.onFetchResultCallbacks.forEach((c) =>
                c(Promise.resolve(null)),
            );
        }
    }

    async fetchQuery({ fetchMore = false } = {}) {
        if (this.disableFetchJobs) {
            return;
        }
        // because a user potentially can trigger a lot of requests (by changing each filter type)
        // we have to avoid too many concurrent requests
        if (this.fetchIsLoading) {
            this.abortFetch();
        }
        this.fetchIsLoading = true;
        this.onLoadingCallbacks?.forEach((fun) => fun(true));

        const searchParams = this.getUrlParams();
        const { page, pageSize } = this?.getPagination() ?? {
            page: 1,
            pageSize: 10,
        };

        // the backend counts from page 0
        searchParams.append('page', page - 1);
        searchParams.append('pageSize', pageSize);

        // add language too
        const languageElement = document.querySelector('[data-job-language]');
        const language = languageElement?.innerText;
        if (language) {
            searchParams.append('language', language);
        }
        else {
            searchParams.append(
                'language',
                window.location.pathname.indexOf('/en/') > -1 ? 'gb' : 'dk',
            );
        }        

        // Retrieve the job brand from the HTML element
        const jobBrandElement = document.querySelector('[data-job-brand]');
        const jobBrand = jobBrandElement?.innerText;
        if (jobBrand) {
            searchParams.append('brand', jobBrand);
        }
        const signal = this.abortController.signal;
        try {
            const response = await fetch(
                GET_JOBS_URL + (searchParams ? `?${searchParams}` : ''),
                {
                    signal,
                },
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const resultPromise = await response.json();

            // ensure to display the selected regions for found jobs
            // if searched by region
            const regionParams = searchParams
                .get(REGION_FILTER_NAME)
                ?.split(URL_PARAMS_SEPERATOR);
            if (regionParams?.length > 0 && resultPromise.totalItems) {
                resultPromise.jobs.forEach((job) => {
                    job.location.zipCodes = job.location.zipCodes.filter(
                        ({ id }) => regionParams.includes(id.toString()),
                    );
                });
            }
            this.onFetchResultCallbacks.forEach((c) =>
                c(resultPromise, fetchMore),
            );
        } catch (error) {
            // no need to log aborts
            if (typeof error !== DOMException && error.name !== 'AbortError') {
                console.error(error);
            }
            this.onFetchResultCallbacks.forEach((c) => c({}, fetchMore));
        } finally {
            this.onLoadingCallbacks?.forEach((fun) => fun(false));
            this.fetchIsLoading = false;
        }
    }
}
